import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of, throwError, timer } from 'rxjs';
import { catchError, filter, map, mergeMap, retryWhen, switchMap, take } from 'rxjs/operators';

import { AppState, ConfigService, SentryLogger } from '@fcom/core';
import { loginToken } from '@fcom/core/selectors';

import {
  Transaction,
  TransactionsRequest,
  TransactionsResponse,
  TransactionsResponsePartStatusCode,
} from '../interfaces';

export enum Status {
  INITIAL,
  LOADING,
  COMPLETE,
  EMPTY,
  ERROR,
  ALL_LOADED,
}

@Injectable()
export class TransactionService {
  private accessToken$: Observable<string>;
  private continuationToken: string;

  status$: BehaviorSubject<Status> = new BehaviorSubject<Status>(Status.INITIAL);
  transactionsStore: Transaction[] = [];

  constructor(
    private http: HttpClient,
    private sentryLogger: SentryLogger,
    private store$: Store<AppState>,
    private configService: ConfigService
  ) {
    this.accessToken$ = this.store$.pipe(loginToken(), filter(Boolean), take(1)) as Observable<string>;
  }

  getTransactions(): Observable<Transaction[]> {
    const apiUrl = this.configService.cfg.loyaltyServices.legacy.apiProfileUrl;
    const xApiKey = this.configService.cfg.loyaltyServices.legacy.apiProfileApiKey;

    const MAX_ATTEMPTS = 50;
    const BACKOFF_MS = 2000;

    if (this.continuationToken === 'END') {
      const status = this.transactionsStore.length ? Status.ALL_LOADED : Status.EMPTY;
      this.status$.next(status);
      return of([]);
    }

    this.status$.next(Status.LOADING);
    return this.accessToken$.pipe(
      switchMap((token) => {
        const httpOptions = {
          headers: new HttpHeaders({
            'x-api-key': xApiKey,
            oauth_token: token,
          }),
        };

        const payload: TransactionsRequest = {
          transactionsRequest: {
            ...(this.continuationToken && { continuationToken: this.continuationToken }),
          },
        };

        return this.http.post<TransactionsResponse>(apiUrl, payload, httpOptions).pipe(
          map((response) => this.handleResponse(response)),
          retryWhen((attempts) =>
            attempts.pipe(
              mergeMap((error, i) => {
                if (i >= MAX_ATTEMPTS || error.message !== 'retry later') {
                  return throwError(() => new Error('Error when requesting transactions'));
                }
                return timer(BACKOFF_MS);
              })
            )
          ),
          // eslint-disable-next-line rxjs/no-implicit-any-catch
          catchError((err) => {
            this.sentryLogger.warn(err.message, { error: err });
            return throwError(() => err);
          })
        );
      })
    );
  }

  private handleResponse(response: TransactionsResponse): Transaction[] {
    if (response.transactions.partStatus.code === TransactionsResponsePartStatusCode.SUCCESS) {
      this.continuationToken = response.transactions.partStatus.continuationToken;
      if (this.continuationToken === 'END') {
        const status =
          this.transactionsStore.length || response.transactions.transactions.length ? Status.ALL_LOADED : Status.EMPTY;
        this.status$.next(status);
      } else {
        this.status$.next(Status.COMPLETE);
      }
      return response.transactions.transactions;
    } else if (response.transactions.partStatus.code === TransactionsResponsePartStatusCode.RETRY_LATER) {
      throw new Error('retry later');
    } else if (response.transactions.partStatus.code === TransactionsResponsePartStatusCode.OTHER_ERROR) {
      this.status$.next(Status.ERROR);
    }
    throw new Error('Error when requesting transactions');
  }
}
